import React, { useEffect, useRef, useState } from 'react';
import { PhotoField } from '../../components/ui';
import api from '../../services/api';
import { eFormFileDownloadPath, uploadEFormFiles } from '../../services/mutations';
import type { EFormFieldSchema, EFormFileMeta } from './schema';
import { isEFormFileMeta } from './schema';

export const EFORM_PHOTO_ACCEPT =
  'image/png,image/jpeg,image/gif,image/webp,image/bmp,.png,.jpg,.jpeg,.gif,.webp,.bmp';

function revokeUrl(url: string | undefined) {
  if (url?.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }
}

function useEFormPhotoBlobUrl(storageKey: string | null | undefined, mimeType?: string) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!storageKey) {
      if (blobUrlRef.current) {
        revokeUrl(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setBlobUrl(null);
      setLoading(false);
      return;
    }
    if (storageKey.startsWith('blob:') || storageKey.startsWith('data:')) {
      setBlobUrl(storageKey);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    api
      .get(eFormFileDownloadPath(storageKey), {
        responseType: 'blob',
        headers: { Accept: 'image/*,application/octet-stream' },
      })
      .then((res) => {
        if (cancelled) return;
        if (blobUrlRef.current) revokeUrl(blobUrlRef.current);
        const raw = res.data as Blob;
        if (res.status === 204 || !raw || raw.size === 0) {
          setBlobUrl(null);
          return;
        }
        const type = (raw.type || mimeType || '').toLowerCase();
        if (type.includes('json') || type.includes('text/')) {
          setBlobUrl(null);
          return;
        }
        const imageBlob =
          type.startsWith('image/') || type === 'application/octet-stream' || !type
            ? new Blob([raw], { type: type.startsWith('image/') ? type : 'image/jpeg' })
            : raw;
        const url = URL.createObjectURL(imageBlob);
        blobUrlRef.current = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        revokeUrl(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [storageKey, mimeType]);

  return { blobUrl, loading };
}

export const EFormPhotoField: React.FC<{
  field: EFormFieldSchema;
  value: unknown;
  onChange: (value: EFormFileMeta | null) => void;
  readOnly?: boolean;
  error?: string;
}> = ({ field, value, onChange, readOnly = false, error }) => {
  const meta = isEFormFileMeta(value) ? value : null;
  const { blobUrl, loading } = useEFormPhotoBlobUrl(meta?.storageKey, meta?.mimeType);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const onPick = async (file: File) => {
    if (readOnly) return;
    setLocalError(null);
    setBusy(true);
    try {
      const [uploaded] = await uploadEFormFiles([file]);
      onChange(uploaded);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as { message?: string })?.message ||
        'Photo upload failed';
      setLocalError(Array.isArray(msg) ? msg.join(', ') : String(msg));
    } finally {
      setBusy(false);
    }
  };

  const onRemove = () => {
    if (readOnly) return;
    setLocalError(null);
    onChange(null);
  };

  return (
    <PhotoField
      variant="avatar"
      size="sm"
      actionsPlacement="side"
      imageUrl={blobUrl}
      loading={loading}
      filename={meta?.fileName}
      hasImage={Boolean(meta)}
      emptyLabel="—"
      editable={!readOnly}
      busy={busy}
      error={error || localError}
      accept={field.accept?.trim() || EFORM_PHOTO_ACCEPT}
      onPick={onPick}
      onRemove={readOnly ? undefined : onRemove}
    />
  );
};

export default EFormPhotoField;
